import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import Cookies from "js-cookie";
import Select from "react-select";
import axios from "axios";
import API_ENDPOINTS from "../config/api";

// 진동 애니메이션
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`;

// 공동신청자 관련 스타일 컴포넌트
const CoApplicantCountBadge = styled.div`
  background-color: #0075ff;
  color: white;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 600;
`;

const CoApplicantCard = styled.div`
  background: white;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
`;

const CoApplicantHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  cursor: pointer;
  background: ${(props) => (props.isOpen ? "#f8f8f8" : "white")};
  border-bottom: ${(props) => (props.isOpen ? "1px solid #eee" : "none")};
  transition: all 0.3s ease;

  &:hover {
    background: #f8f8f8;
  }
`;

const CoApplicantInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CoApplicantName = styled.h4`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #333;
`;

const CoApplicantPhone = styled.span`
  font-size: 14px;
  color: #666;
`;

const CoApplicantStatus = styled.div`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${(props) =>
    props.status === "VERIFIED"
      ? "#4caf50"
      : props.status === "PENDING"
      ? "#ff9800"
      : "#f44336"};
  color: white;
`;

const CoApplicantDetails = styled(motion.div)`
  padding: ${(props) => (props.isOpen ? "16px" : "0 16px")};
  background: white;
  overflow: hidden;
`;

const DetailSection = styled.div`
  margin-top: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  color: #666;
  margin: 0 0 8px 0;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-size: 14px;
  color: #666;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const ToggleIcon = styled.img`
  width: 24px;
  height: 24px;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.3s ease;
`;

// 스타일 컴포넌트를 컴포넌트 외부로 이동

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f8f8f8;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  overflow: visible;

  @media (min-width: 769px) {
    padding: 20px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;
  overflow: visible;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: 100vh;
    box-shadow: none;
    padding: 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 768px) {
    background-color: #ffffff;
    border-radius: 20px;
    min-height: auto;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
    max-height: none;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: visible;

  @media (max-width: 768px) {
    min-height: 100%;
  }

  @media (min-width: 769px) {
    min-height: auto;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  position: relative;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  position: absolute;
  left: 20px;
  padding: 0;
`;

const BackIcon = styled.span`
  font-size: 24px;
  font-weight: bold;
  color: #333;
`;

const PageTitle = styled.h1`
  font-size: 17px;
  font-weight: 600;
  flex: 1;
  text-align: center;
  margin: 0;
  color: #00285b;
`;

const StatusSection = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 120px; /* 하단 버튼과 푸터를 위한 공간 확보 */
`;

const SectionSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 30px 0;
`;

const DetailBox = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 0;
  margin-bottom: 0px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const DetailBox2 = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const BoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: #333;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #eee;
  margin: 15px 0;
`;

const RowGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
  margin-top: 8px;
`;

const StatusBadge = styled.div`
  background-color: ${(props) => (props.isWaiting ? "#ff9800" : "#007bff")};
  color: white;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 4px;
  display: inline-block;
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin-top: 30px;
  padding-bottom: 20px;
  margin-bottom: 30px; /* 푸터와의 간격 확보를 위해 늘림 */
`;

const HomeButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #007bff;
  color: white;
  width: 100%;
  height: 56px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0069d9;
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 15px;
`;

const ColumnLabel = styled.span`
  color: #666;
  font-size: 15px;
`;

const ColumnTimeValue = styled.span`
  font-weight: 500;
  color: #64748b;
  font-size: 14px;
  text-align: right;
`;

const ColumnValue = styled.span`
  font-weight: 500;
  color: #333;
  font-size: 15px;
  text-align: right;
`;

const FullDivider = styled.div`
  height: 1px;
  background-color: #eee;
  width: 100%;
`;

const SectionLabel = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #333;
`;

// 이미지 모달 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;
const ModalContainer = styled(motion.div)`
  width: 90%;
  max-width: 400px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 32px 24px 24px 24px;
`;

const ModalConfirmButton = styled.button`
  height: 44px;
  width: 100%;
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

const ImageModal = styled(motion.div)`
  position: relative;
  max-width: 90%;
  max-height: 90vh;
  overflow: hidden;
  border-radius: 8px;
  background-color: white;
`;

const FullScreenImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

// 로딩 & 에러 컴포넌트
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 20px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 123, 255, 0.2);
  border-radius: 50%;
  border-top-color: #007bff;
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  font-size: 16px;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 20px;
  padding: 20px;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #dc3545;
  text-align: center;
  max-width: 400px;
`;

// 새로운 스타일 컴포넌트
const EditModal = styled(motion.div)`
  position: relative;
  width: 90%;
  max-width: 400px;
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #222;
  margin: 0 0 18px 0;
  text-align: left;
`;

const ModalContent = styled.div`
  padding: 0 0 28px 0;
  font-size: 16px;
  color: #333;
  text-align: left;
  line-height: 1.6;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  padding: 0;
  margin-top: 8px;
`;

const ModalCancelButton = styled.button`
  flex: 1;
  height: 56px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background-color: #f8f8f8;
  color: #666;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #eee;
  }
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background-color: #007bff;
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    background-color: #0069d9;
  }
`;

// 계좌 정보 관련 스타일 추가
const BoxTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const EditButton = styled.button`
  background-color: transparent;
  color: #0075ff;
  border: 1px solid #0075ff;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(0, 117, 255, 0.1);
  }
`;

const EmptyAccountInfo = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #666;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`;

const RegisterAccountButton = styled.button`
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #0069d9;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
`;

const BankInput = styled.input`
  flex: 1;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: white;
  width: 100%;
  transition: border-color 0.2s;

  &::placeholder {
    color: #999;
  }

  &:focus {
    border-color: #0075ff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 117, 255, 0.1);
  }
`;

// 커스텀 옵션 컴포넌트를 컴포넌트 외부로 이동
const OptionContainer = styled.div`
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  min-height: 40px;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const SingleValueContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  position: relative;
  top: 50%;
  transform: translateY(-50%);
`;

const BankIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

const Required = styled.span`
  color: #fe7156;
  margin-left: 2px;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #2e3840;
`;

const BankIconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const BankIconSmall = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

// InputContainer 수정
const InputContainer = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const ErrorText = styled.p`
  color: #ff4c4c;
  font-size: 14px;
  margin-top: 8px;
  text-align: left;
`;

// Select 스타일 컴포넌트도 바깥으로 이동
const BankSelect = styled(Select)`
  .select__control {
    height: 50px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    box-shadow: none;
    background: white;

    &:hover {
      border-color: #0075ff;
    }

    &--is-focused {
      border-color: #0075ff;
      box-shadow: none;
    }
  }

  .select__placeholder {
    color: #999;
  }

  .select__single-value {
    color: #333;
    margin: 0;
    padding: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  .select__value-container {
    padding: 2px 16px;
    cursor: pointer;
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;
  }

  .select__menu {
    margin-top: 4px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .select__option {
    &--is-focused {
      background-color: #f5f5f5;
    }

    &--is-selected {
      background-color: #e6f0ff;
      color: #0075ff;
    }
  }
`;

// React 컴포넌트를 위한 props 정의 (React 컴포넌트이므로 컴포넌트 외부로 이동)
const CustomOption = ({ innerProps, label, data }) => (
  <OptionContainer {...innerProps}>
    <BankIcon src={data.icon} alt={label} />
    <span>{label}</span>
  </OptionContainer>
);

const CustomSingleValue = ({ children, data }) => (
  <SingleValueContainer>
    <BankIcon src={data.icon} alt={children} />
    <span>{children}</span>
  </SingleValueContainer>
);

// 시작 부분에 컴포넌트 스타일 추가
const FlexColumnLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

// 흔들림 애니메이션 스타일 정의
const ShakingInputContainer = styled(InputContainer)`
  animation: ${shake} 0.5s;
`;

// 스타일 컴포넌트 추가 (더 많은 스타일 컴포넌트가 정의된 곳에 추가)
const ConfirmMessage = styled.p`
  font-size: 18px;
  font-weight: 600;
  text-align: left;
  margin: 20px 0 10px;
  color: #333;
`;

const WarningMessage = styled.p`
  font-size: 14px;
  text-align: left;
  margin: 0 0 25px;
  color: #dc3545;
  line-height: 1.5;
`;

// CancelButton 스타일 컴포넌트 추가
const CancelButton = styled(motion.button)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff;
  color: #dc3545;
  width: 100%;
  height: 56px;
  border-radius: 8px;
  border: 1px solid #dc3545;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 12px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #fff0f0;
  }
`;

// 새로운 스타일 컴포넌트 추가 - 취소 확인 버튼
const CancelConfirmButton = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  background-color: #dc3545;
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    background-color: #c82333;
  }
`;

const StatusPage = () => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const [applicationData, setApplicationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // 계좌 정보 관련 상태 추가
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [accountInfo, setAccountInfo] = useState({
    bankCode: null,
    accountNumber: null,
    accountHolder: null,
  });
  const [accountError, setAccountError] = useState("");
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [accountErrors, setAccountErrors] = useState({
    bankCode: "",
    accountNumber: "",
    accountHolder: "",
  });
  const [shakingFields, setShakingFields] = useState({
    bankCode: false,
    accountNumber: false,
    accountHolder: false,
  });

  const accountNumberRef = useRef(null);
  const accountHolderRef = useRef(null);
  const bankSelectRef = useRef(null);

  // 포커스 유지를 위한 상태
  const [accountNumberKey, setAccountNumberKey] = useState(0);
  const [accountHolderKey, setAccountHolderKey] = useState(0);

  // useRef로 현재 편집 중인 필드 추적을 위한 상태 추가
  const [editingField, setEditingField] = useState(null);

  // 필드에 포커스가 들어올 때 호출되는 함수
  const handleFieldFocus = (field) => () => {
    setEditingField(field);
  };

  // 필드에서 포커스가 벗어날 때 호출되는 함수
  const handleFieldBlur = (field) => () => {
    setEditingField(null);

    // 포커스가 사라질 때 유효성 검사 수행
    if (field === "accountNumber" && accountInfo.accountNumber) {
      const error = validateAccountNumber(accountInfo.accountNumber);
      if (error) {
        setAccountErrors((prev) => ({ ...prev, accountNumber: error }));
      }
    } else if (field === "accountHolder" && accountInfo.accountHolder) {
      const error = validateAccountHolder(accountInfo.accountHolder);
      if (error) {
        setAccountErrors((prev) => ({ ...prev, accountHolder: error }));
      }
    }
  };

  // 은행 목록 데이터
  const bankOptions = [
    { value: "KB", label: "KB국민은행", icon: "/bank-icons/kb.png" },
    { value: "shinhan", label: "신한은행", icon: "/bank-icons/shinhan.png" },
    { value: "woori", label: "우리은행", icon: "/bank-icons/woori.png" },
    { value: "smk", label: "새마을금고", icon: "/bank-icons/smk.png" },
    { value: "hana", label: "하나은행", icon: "/bank-icons/hana.png" },
    { value: "nh", label: "농협은행", icon: "/bank-icons/nh.png" },
    { value: "sh", label: "신협은행", icon: "/bank-icons/sh.png" },
    { value: "ibk", label: "기업은행", icon: "/bank-icons/ibk.png" },
    { value: "sc", label: "SC제일은행", icon: "/bank-icons/sc.png" },
    { value: "kakao", label: "카카오뱅크", icon: "/bank-icons/kakao.png" },
    { value: "toss", label: "토스뱅크", icon: "/bank-icons/toss.png" },
    { value: "kdb", label: "산업은행", icon: "/bank-icons/kdb.png" },
    { value: "city", label: "한국씨티은행", icon: "/bank-icons/city.png" },
    { value: "dgb", label: "대구은행", icon: "/bank-icons/dgb.png" },
    { value: "bnk", label: "부산은행", icon: "/bank-icons/bnk.png" },
    { value: "kjb", label: "광주은행", icon: "/bank-icons/kjb.png" },
    { value: "knb", label: "경남은행", icon: "/bank-icons/bnk.png" },
    { value: "kbank", label: "케이뱅크", icon: "/bank-icons/kbank.png" },
    { value: "suhyup", label: "Sh수협은행", icon: "/bank-icons/suhyup.png" },
    { value: "jeju", label: "제주은행", icon: "/bank-icons/jeju.png" },
    { value: "post", label: "우체국", icon: "/bank-icons/post.png" },
  ];

  // 키 이벤트 핸들러 추가 - 계좌번호에 숫자만 입력 가능하도록
  const handleAccountNumberKeyDown = (e) => {
    // 숫자와 하이픈, 백스페이스, 탭, 화살표 키 등 제어 키만 허용
    const allowedKeys =
      /^[0-9-]|Backspace|Tab|ArrowLeft|ArrowRight|Delete|Control|Meta|Alt|Shift$/;
    if (!allowedKeys.test(e.key)) {
      e.preventDefault();
    }
  };

  // 계좌 정보 입력 핸들러 - 완전히 새로운 방식으로 구현
  const handleAccountChange = (field) => (e) => {
    const value = e.target.value;

    // 상태 업데이트가 즉시 이루어지도록 변경
    setAccountInfo((prev) => ({
      ...prev,
      [field]: value,
    }));

    // 입력 중에는 에러 메시지 초기화
    if (accountErrors[field]) {
      setAccountErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  // 은행 선택 핸들러
  const handleBankChange = (selectedOption) => {
    setAccountInfo({
      ...accountInfo,
      bankCode: selectedOption ? selectedOption.label : null,
    });
    if (accountErrors.bankCode) {
      setAccountErrors((prev) => ({ ...prev, bankCode: "" }));
    }
  };

  // 신청 데이터 가져오기
  useEffect(() => {
    setIsLoading(true);
    try {
      // sessionStorage에서 applicationData 가져오기
      const storedData = sessionStorage.getItem("applicationData");

      if (!storedData) {
        throw new Error("신청 내역을 찾을 수 없습니다.");
      }

      const data = JSON.parse(storedData);
      setApplicationData(data);

      // 계좌 정보 설정
      if (data.bankCode && data.accountNumber && data.accountHolder) {
        setAccountInfo({
          bankCode: data.bankCode,
          accountNumber: data.accountNumber,
          accountHolder: data.accountHolder,
        });
      } else {
        // 계좌 정보가 없으면 쿠키에서 가져오기 시도
        const bankCode = Cookies.get("bankCode");
        const accountNumber = Cookies.get("accountNumber");
        const accountHolder = Cookies.get("accountHolder");

        if (bankCode && accountNumber && accountHolder) {
          setAccountInfo({
            bankCode,
            accountNumber,
            accountHolder,
          });
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 날짜 포맷 함수
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")} ${String(
      date.getHours()
    ).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  const modalVariants = {
    initial: {
      opacity: 0,
      scale: 0.8,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
      },
    },
  };

  // 계좌 모달 열기
  const openAccountModal = () => {
    // 모달 열 때마다 키 초기화하여 제대로 렌더링되도록 함
    setAccountNumberKey((prev) => prev + 1);
    setAccountHolderKey((prev) => prev + 1);

    // 기존 계좌 정보가 있으면 해당 은행코드로 bankCode 설정
    setShowAccountModal(true);
    setAccountError("");
    setAccountErrors({
      bankCode: "",
      accountNumber: "",
      accountHolder: "",
    });
    setShakingFields({
      bankCode: false,
      accountNumber: false,
      accountHolder: false,
    });
  };

  // 계좌 모달 닫기
  const closeAccountModal = () => {
    setShowAccountModal(false);
    setAccountError("");
    setAccountErrors({
      bankCode: "",
      accountNumber: "",
      accountHolder: "",
    });
  };

  // 계좌번호 유효성 검사
  const validateAccountNumber = (accountNumber) => {
    // 숫자와 하이픈만 허용
    const accountRegex = /^[0-9-]+$/;
    if (!accountRegex.test(accountNumber)) {
      return "계좌번호는 숫자만 입력 가능합니다.";
    }
    return "";
  };

  // 예금주명 유효성 검사
  const validateAccountHolder = (accountHolder) => {
    if (accountHolder.trim().length < 2) {
      return "예금주명을 정확히 입력해주세요.";
    }
    return "";
  };

  // 계좌 정보 유효성 검사
  const validateBankInfo = () => {
    const bankCodeError = !accountInfo.bankCode ? "은행을 선택해주세요." : "";
    const accountNumberError = validateAccountNumber(accountInfo.accountNumber);
    const accountHolderError = validateAccountHolder(accountInfo.accountHolder);
    let isValid = true;

    setAccountErrors({
      bankCode: bankCodeError,
      accountNumber: accountNumberError,
      accountHolder: accountHolderError,
    });

    // 모든 흔들림 상태 초기화
    setShakingFields({
      bankCode: false,
      accountNumber: false,
      accountHolder: false,
    });

    // 오류가 있는 필드에 흔들림 적용 후 일정 시간 후 해제
    if (bankCodeError) {
      setShakingFields((prev) => ({ ...prev, bankCode: true }));
      setTimeout(() => {
        setShakingFields((prev) => ({ ...prev, bankCode: false }));
      }, 500);
      isValid = false;
    } else if (accountNumberError) {
      accountNumberRef.current.focus();
      setShakingFields((prev) => ({ ...prev, accountNumber: true }));
      setTimeout(() => {
        setShakingFields((prev) => ({ ...prev, accountNumber: false }));
      }, 500);
      isValid = false;
    } else if (accountHolderError) {
      accountHolderRef.current.focus();
      setShakingFields((prev) => ({ ...prev, accountHolder: true }));
      setTimeout(() => {
        setShakingFields((prev) => ({ ...prev, accountHolder: false }));
      }, 500);
      isValid = false;
    }

    return isValid;
  };

  // 계좌 정보 저장
  const saveAccountInfo = async () => {
    // 유효성 검사
    if (!validateBankInfo()) {
      return;
    }

    setIsSavingAccount(true);
    setAccountError("");

    try {
      // API 요청 본문 구성
      const requestBody = {
        bankCode: accountInfo.bankCode,
        accountNumber: accountInfo.accountNumber,
        accountHolder: accountInfo.accountHolder,
      };

      // 계좌 정보 업데이트 API 호출 - 경로 수정
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/applications/number/${applicationData.applicationNumber}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "계좌 정보 저장에 실패했습니다.");
      }

      // 계좌 정보 업데이트
      setApplicationData({
        ...applicationData,
        ...requestBody,
      });

      // 쿠키에도 저장
      Cookies.set("bankCode", accountInfo.bankCode);
      Cookies.set("accountNumber", accountInfo.accountNumber);
      Cookies.set("accountHolder", accountInfo.accountHolder);

      // 모달 닫기
      closeAccountModal();
    } catch (error) {
      setAccountError(
        error.message || "계좌 정보 저장 중 오류가 발생했습니다."
      );
    } finally {
      setIsSavingAccount(false);
    }
  };

  // 은행 코드로 은행명 가져오기
  const getBankName = (code) => {
    const bank = bankOptions.find((bank) => bank.value === code);
    return bank ? bank.label : code;
  };

  // 새 모달 상태 추가 (useState 선언 부분에 추가)
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");

  // 소송 취소 모달 열기/닫기 함수 수정
  const openCancelModal = () => {
    setShowNotAllowedModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setCancelError("");
    // 모달이 닫힐 때 body의 스크롤을 다시 활성화
    document.body.style.overflow = "auto";
  };

  // 소송 취소 API 호출 함수 추가 (API 호출 함수들 근처에 추가)
  const handleCancelLawsuit = async () => {
    if (!applicationData?.id) {
      setCancelError("신청 정보를 찾을 수 없습니다.");
      return;
    }

    setIsCancelling(true);
    setCancelError("");

    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/applications/${applicationData.id}?removePaymentAndOrder=false`,
        {
          method: "DELETE",
          headers: {},
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "소송 취소에 실패했습니다.");
      }

      // 소송 취소 성공 후 홈으로 이동
      navigate("/");
    } catch (error) {
      setCancelError(error.message || "서버 통신 중 오류가 발생했습니다.");
    } finally {
      setIsCancelling(false);
    }
  };

  const [openStates, setOpenStates] = useState([]);

  // 드롭다운 토글 핸들러 추가
  const toggleDropdown = (index) => {
    setOpenStates((prev) => {
      const newStates = [...prev];
      newStates[index] = !newStates[index];
      return newStates;
    });
  };

  // 공동신청자 계좌 정보 수정 모달 관련 상태 추가
  const [showCoApplicantAccountModal, setShowCoApplicantAccountModal] =
    useState(false);
  const [selectedCoApplicant, setSelectedCoApplicant] = useState(null);
  const [coApplicantAccountInfo, setCoApplicantAccountInfo] = useState({
    bankCode: null,
    accountNumber: null,
    accountHolder: null,
  });
  const [isUpdatingCoApplicant, setIsUpdatingCoApplicant] = useState(false);
  const [coApplicantAccountError, setCoApplicantAccountError] = useState("");

  // 공동신청자 계좌 정보 수정 모달 열기
  const openCoApplicantAccountModal = (coApplicant) => {
    setSelectedCoApplicant(coApplicant);
    setCoApplicantAccountInfo({
      bankCode: coApplicant.bankCode,
      accountNumber: coApplicant.accountNumber,
      accountHolder: coApplicant.accountHolder,
    });
    setShowCoApplicantAccountModal(true);
    setCoApplicantAccountError("");
    setAccountErrors({
      bankCode: "",
      accountNumber: "",
      accountHolder: "",
    });
  };

  // 공동신청자 계좌 정보 수정 모달 닫기
  const closeCoApplicantAccountModal = () => {
    setShowCoApplicantAccountModal(false);
    setSelectedCoApplicant(null);
    setCoApplicantAccountError("");
  };

  // 공동신청자 계좌 정보 저장
  const saveCoApplicantAccountInfo = async () => {
    if (!validateBankInfo()) {
      return;
    }

    setIsUpdatingCoApplicant(true);
    setCoApplicantAccountError("");

    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/co-applicants/${selectedCoApplicant.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            bankCode: coApplicantAccountInfo.bankCode,
            accountNumber: coApplicantAccountInfo.accountNumber,
            accountHolder: coApplicantAccountInfo.accountHolder,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "계좌 정보 수정에 실패했습니다.");
      }

      // 성공 시 applicationData 업데이트
      setApplicationData((prevData) => ({
        ...prevData,
        coApplicants: prevData.coApplicants.map((coApp) =>
          coApp.id === selectedCoApplicant.id
            ? {
                ...coApp,
                bankCode: coApplicantAccountInfo.bankCode,
                accountNumber: coApplicantAccountInfo.accountNumber,
                accountHolder: coApplicantAccountInfo.accountHolder,
              }
            : coApp
        ),
      }));

      closeCoApplicantAccountModal();
    } catch (error) {
      setCoApplicantAccountError(
        error.message || "계좌 정보 수정 중 오류가 발생했습니다."
      );
    } finally {
      setIsUpdatingCoApplicant(false);
    }
  };

  // 소송 취하 불가 안내 모달 상태 추가
  const [showNotAllowedModal, setShowNotAllowedModal] = useState(false);

  const closeNotAllowedModal = () => {
    setShowNotAllowedModal(false);
    document.body.style.overflow = "auto";
  };

  if (isLoading) {
    return (
      <PageBackground>
        <LoadingContainer>
          <Spinner />
          <LoadingText>신청 내역을 불러오는 중입니다...</LoadingText>
        </LoadingContainer>
      </PageBackground>
    );
  }

  if (error) {
    return (
      <PageBackground>
        <ErrorContainer>
          <ErrorMessage>{error}</ErrorMessage>
          <HomeButton onClick={() => navigate("/")}>홈으로 돌아가기</HomeButton>
        </ErrorContainer>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <Header>
            {/* <BackButton onClick={() => navigate(-1)}>
              <BackIcon>&lt;</BackIcon>
            </BackButton> */}
            <PageTitle>상세 내역보기</PageTitle>
          </Header>

          <StatusSection>
            <SectionTitle>소송접수 조회</SectionTitle>
            <SectionSubtitle>
              {applicationData?.name || "사용자"}님의 집단 소송 접수내역입니다.
            </SectionSubtitle>

            <DetailBox2>
              <BoxTitle>결제 금액</BoxTitle>
              <DetailRow>
                <DetailLabel>접수비</DetailLabel>
                <DetailValue>
                  {applicationData?.baseFee
                    ? `${Number(applicationData.baseFee).toLocaleString()}원`
                    : "-"}
                </DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>인원 수</DetailLabel>
                <DetailValue>
                  {`${1 + (applicationData?.coApplicants?.length || 0)}명`}
                </DetailValue>
              </DetailRow>
              <Divider />
              <DetailRow>
                <DetailLabel>결제 완료 금액</DetailLabel>
                <DetailValue>
                  {applicationData?.baseFee
                    ? `${Number(
                        applicationData.baseFee *
                          (1 + (applicationData?.coApplicants?.length || 0))
                      ).toLocaleString()}원`
                    : "-"}
                </DetailValue>
              </DetailRow>
            </DetailBox2>

            <DetailBox2>
              <BoxTitleRow>
                <BoxTitle>계좌 정보</BoxTitle>
                {accountInfo.bankCode &&
                  accountInfo.accountNumber &&
                  accountInfo.accountHolder && (
                    <EditButton onClick={openAccountModal}>수정</EditButton>
                  )}
              </BoxTitleRow>

              {accountInfo.bankCode &&
              accountInfo.accountNumber &&
              accountInfo.accountHolder ? (
                <>
                  <DetailRow>
                    <DetailLabel>은행</DetailLabel>
                    <DetailValue>
                      {bankOptions.find(
                        (bank) => bank.label === accountInfo.bankCode
                      ) && (
                        <BankIconContainer>
                          <BankIconSmall
                            src={
                              bankOptions.find(
                                (bank) => bank.label === accountInfo.bankCode
                              ).icon
                            }
                            alt={accountInfo.bankCode}
                          />
                          {accountInfo.bankCode}
                        </BankIconContainer>
                      )}
                    </DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>계좌번호</DetailLabel>
                    <DetailValue>{accountInfo.accountNumber}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>예금주</DetailLabel>
                    <DetailValue>{accountInfo.accountHolder}</DetailValue>
                  </DetailRow>
                </>
              ) : (
                <EmptyAccountInfo>
                  계좌 정보가 등록되어 있지 않습니다.
                  <RegisterAccountButton onClick={openAccountModal}>
                    계좌 등록하기
                  </RegisterAccountButton>
                </EmptyAccountInfo>
              )}
            </DetailBox2>

            {/* 공동신청자 정보 */}
            {applicationData?.coApplicants &&
              applicationData.coApplicants.length > 0 && (
                <DetailBox2>
                  <BoxTitleRow>
                    <BoxTitle>공동신청자 정보</BoxTitle>
                    <CoApplicantCountBadge>
                      {applicationData.coApplicants.length}명
                    </CoApplicantCountBadge>
                  </BoxTitleRow>

                  {applicationData.coApplicants.map((coApplicant, index) => (
                    <CoApplicantCard key={coApplicant.id}>
                      <CoApplicantHeader
                        onClick={() => toggleDropdown(index)}
                        isOpen={openStates[index]}
                      >
                        <CoApplicantInfo>
                          <CoApplicantName>
                            {coApplicant.client.name}
                          </CoApplicantName>
                          <CoApplicantPhone>
                            {coApplicant.client.phoneNumber}
                          </CoApplicantPhone>
                        </CoApplicantInfo>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <CoApplicantStatus
                            status={coApplicant.signatureStatus}
                          >
                            {coApplicant.signatureStatus === "VERIFIED"
                              ? "서명 완료"
                              : coApplicant.signatureStatus === "PENDING"
                              ? "서명 대기중"
                              : "서명 필요"}
                          </CoApplicantStatus>
                          <ToggleIcon
                            src="/png/dropDown.png"
                            alt="dropdown"
                            isOpen={openStates[index]}
                          />
                        </div>
                      </CoApplicantHeader>

                      <AnimatePresence>
                        {openStates[index] && (
                          <CoApplicantDetails
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <DetailSection>
                              <SectionTitle>기본 정보</SectionTitle>
                              <DetailRow>
                                <DetailLabel>생년월일</DetailLabel>
                                <DetailValue>
                                  {coApplicant.client.birthDate
                                    ? `${coApplicant.client.birthDate.substring(
                                        0,
                                        4
                                      )}년 ${coApplicant.client.birthDate.substring(
                                        4,
                                        6
                                      )}월 ${coApplicant.client.birthDate.substring(
                                        6,
                                        8
                                      )}일`
                                    : "-"}
                                </DetailValue>
                              </DetailRow>
                              <DetailRow>
                                <DetailLabel>이메일</DetailLabel>
                                <DetailValue>
                                  {coApplicant.client.email || "-"}
                                </DetailValue>
                              </DetailRow>
                              <DetailRow>
                                <DetailLabel>주소</DetailLabel>
                                <DetailValue>
                                  {coApplicant.client.address || "-"}
                                </DetailValue>
                              </DetailRow>
                            </DetailSection>

                            <DetailSection>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: "12px",
                                }}
                              >
                                <SectionTitle>계좌 정보</SectionTitle>
                                <EditButton
                                  onClick={() =>
                                    openCoApplicantAccountModal(coApplicant)
                                  }
                                >
                                  수정
                                </EditButton>
                              </div>
                              <DetailRow>
                                <DetailLabel>은행</DetailLabel>
                                <DetailValue>
                                  {coApplicant.bankCode && (
                                    <BankIconContainer>
                                      <BankIconSmall
                                        src={
                                          bankOptions.find(
                                            (bank) =>
                                              bank.label ===
                                              coApplicant.bankCode
                                          )?.icon
                                        }
                                        alt={coApplicant.bankCode}
                                      />
                                      {coApplicant.bankCode}
                                    </BankIconContainer>
                                  )}
                                </DetailValue>
                              </DetailRow>
                              <DetailRow>
                                <DetailLabel>계좌번호</DetailLabel>
                                <DetailValue>
                                  {coApplicant.accountNumber || "-"}
                                </DetailValue>
                              </DetailRow>
                              <DetailRow>
                                <DetailLabel>예금주</DetailLabel>
                                <DetailValue>
                                  {coApplicant.accountHolder || "-"}
                                </DetailValue>
                              </DetailRow>
                            </DetailSection>
                          </CoApplicantDetails>
                        )}
                      </AnimatePresence>
                    </CoApplicantCard>
                  ))}
                </DetailBox2>
              )}

            <DetailBox>
              <RowGroup>
                <Row>
                  <FlexColumnLeft>
                    <ColumnLabel>처리 내역</ColumnLabel>
                    <ColumnTimeValue>
                      {formatDate(applicationData?.createdAt)}
                    </ColumnTimeValue>
                  </FlexColumnLeft>

                  <ColumnValue>
                    <StatusBadge
                      isWaiting={
                        applicationData?.coApplicants &&
                        applicationData.coApplicants.length > 0 &&
                        applicationData.coApplicants.some(
                          (app) => app.signatureStatus !== "VERIFIED"
                        )
                      }
                    >
                      {applicationData?.coApplicants &&
                      applicationData.coApplicants.length > 0 &&
                      applicationData.coApplicants.some(
                        (app) => app.signatureStatus !== "VERIFIED"
                      )
                        ? "서명 대기중"
                        : "완료됨"}
                    </StatusBadge>
                  </ColumnValue>
                </Row>

                <FullDivider />

                <Row>
                  <ColumnLabel>접수번호</ColumnLabel>
                  <ColumnValue>
                    {applicationData?.applicationNumber?.substring(0, 8) || "-"}
                  </ColumnValue>
                </Row>

                <Row>
                  <ColumnLabel>처리자</ColumnLabel>
                  <ColumnValue>
                    {applicationData?.name || "-"}(본인)
                  </ColumnValue>
                </Row>
              </RowGroup>
            </DetailBox>
            <ButtonContainer>
              <CancelButton onClick={openCancelModal}>
                소송 취소하기
              </CancelButton>
              <HomeButton onClick={() => navigate("/")}>
                홈으로 돌아가기
              </HomeButton>
            </ButtonContainer>
          </StatusSection>
        </Content>
      </Container>

      {/* 계좌 정보 모달 */}
      <AnimatePresence>
        {showAccountModal && (
          <ModalOverlay onClick={(e) => e.stopPropagation()}>
            <EditModal
              variants={modalVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>계좌 정보 수정</ModalTitle>
                <CloseButton onClick={closeAccountModal}>&times;</CloseButton>
              </ModalHeader>

              <ModalContent>
                {accountError && <ErrorMessage>{accountError}</ErrorMessage>}

                <InputContainer>
                  <Label>
                    은행 선택<Required>*</Required>
                  </Label>
                  <BankSelect
                    ref={bankSelectRef}
                    options={bankOptions}
                    value={
                      accountInfo.bankCode
                        ? bankOptions.find(
                            (option) => option.label === accountInfo.bankCode
                          )
                        : null
                    }
                    onChange={handleBankChange}
                    placeholder="은행을 선택해주세요"
                    isSearchable={true}
                    classNamePrefix="select"
                    noOptionsMessage={() => "검색 결과가 없습니다"}
                    loadingMessage={() => "검색 중..."}
                    menuPortalTarget={document.body}
                    components={{
                      Option: CustomOption,
                      SingleValue: CustomSingleValue,
                    }}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                  {accountErrors.bankCode && (
                    <ErrorText>{accountErrors.bankCode}</ErrorText>
                  )}
                </InputContainer>

                {shakingFields.accountNumber ? (
                  <ShakingInputContainer>
                    <Label>
                      계좌번호<Required>*</Required>
                    </Label>
                    <BankInput
                      key={`account-number-${accountNumberKey}`}
                      ref={accountNumberRef}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={accountInfo.accountNumber || ""}
                      onChange={handleAccountChange("accountNumber")}
                      placeholder="계좌번호를 입력해주세요"
                      onFocus={handleFieldFocus("accountNumber")}
                      onBlur={handleFieldBlur("accountNumber")}
                      autoComplete="off"
                      onKeyDown={handleAccountNumberKeyDown}
                    />
                    {accountErrors.accountNumber && (
                      <ErrorText>{accountErrors.accountNumber}</ErrorText>
                    )}
                  </ShakingInputContainer>
                ) : (
                  <InputContainer>
                    <Label>
                      계좌번호<Required>*</Required>
                    </Label>
                    <BankInput
                      key={`account-number-${accountNumberKey}`}
                      ref={accountNumberRef}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={accountInfo.accountNumber || ""}
                      onChange={handleAccountChange("accountNumber")}
                      placeholder="계좌번호를 입력해주세요"
                      onFocus={handleFieldFocus("accountNumber")}
                      onBlur={handleFieldBlur("accountNumber")}
                      autoComplete="off"
                      onKeyDown={handleAccountNumberKeyDown}
                    />
                    {accountErrors.accountNumber && (
                      <ErrorText>{accountErrors.accountNumber}</ErrorText>
                    )}
                  </InputContainer>
                )}

                {shakingFields.accountHolder ? (
                  <ShakingInputContainer>
                    <Label>
                      예금주명<Required>*</Required>
                    </Label>
                    <BankInput
                      key={`account-holder-${accountHolderKey}`}
                      ref={accountHolderRef}
                      type="text"
                      value={accountInfo.accountHolder || ""}
                      onChange={handleAccountChange("accountHolder")}
                      placeholder="예금주명을 입력해주세요"
                      onFocus={handleFieldFocus("accountHolder")}
                      onBlur={handleFieldBlur("accountHolder")}
                      autoComplete="off"
                      disabled={
                        accountInfo.accountHolder !== "" &&
                        accountInfo.accountHolder !== null
                          ? true
                          : false
                      }
                      style={{
                        backgroundColor:
                          accountInfo.accountHolder !== "" &&
                          accountInfo.accountHolder !== null
                            ? "#f5f5f5"
                            : "#fff",
                        color:
                          accountInfo.accountHolder !== "" &&
                          accountInfo.accountHolder !== null
                            ? "#666"
                            : "black",
                      }}
                    />
                    {accountErrors.accountHolder && (
                      <ErrorText>{accountErrors.accountHolder}</ErrorText>
                    )}
                  </ShakingInputContainer>
                ) : (
                  <InputContainer>
                    <Label>
                      예금주명<Required>*</Required>
                    </Label>
                    <BankInput
                      key={`account-holder-${accountHolderKey}`}
                      ref={accountHolderRef}
                      type="text"
                      value={accountInfo.accountHolder || ""}
                      onChange={handleAccountChange("accountHolder")}
                      placeholder="예금주명을 입력해주세요"
                      onFocus={handleFieldFocus("accountHolder")}
                      onBlur={handleFieldBlur("accountHolder")}
                      autoComplete="off"
                      disabled={
                        accountInfo.accountHolder !== "" &&
                        accountInfo.accountHolder !== null
                          ? true
                          : false
                      }
                      style={{
                        backgroundColor:
                          accountInfo.accountHolder !== "" &&
                          accountInfo.accountHolder !== null
                            ? "#f5f5f5"
                            : "#fff",
                        color:
                          accountInfo.accountHolder !== "" &&
                          accountInfo.accountHolder !== null
                            ? "#666"
                            : "black",
                      }}
                    />
                    {accountErrors.accountHolder && (
                      <ErrorText>{accountErrors.accountHolder}</ErrorText>
                    )}
                  </InputContainer>
                )}

                <ModalButtonGroup>
                  <ModalCancelButton onClick={closeAccountModal}>
                    취소
                  </ModalCancelButton>
                  <ConfirmButton
                    onClick={saveAccountInfo}
                    disabled={isSavingAccount}
                  >
                    {isSavingAccount ? "저장 중..." : "저장"}
                  </ConfirmButton>
                </ModalButtonGroup>
              </ModalContent>
            </EditModal>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* 소송 취소 확인 모달 */}
      <AnimatePresence>
        {showCancelModal && (
          <ModalOverlay onClick={(e) => e.stopPropagation()}>
            <EditModal
              variants={modalVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>소송 취소 확인</ModalTitle>
              </ModalHeader>

              <ModalContent>
                {cancelError && <ErrorMessage>{cancelError}</ErrorMessage>}

                <ConfirmMessage>
                  집단소송 참여를 취소하시겠습니까?
                </ConfirmMessage>
                <WarningMessage>
                  소송 취소 시 환불은 불가능하며,
                  <br /> 다시 참여하려면 처음부터 신청 절차를 진행해야 합니다.
                </WarningMessage>

                <ModalButtonGroup>
                  <ModalCancelButton onClick={closeCancelModal}>
                    취소
                  </ModalCancelButton>
                  <CancelConfirmButton
                    onClick={handleCancelLawsuit}
                    disabled={isCancelling}
                  >
                    {isCancelling ? "처리 중..." : "소송 취소하기"}
                  </CancelConfirmButton>
                </ModalButtonGroup>
              </ModalContent>
            </EditModal>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* 공동신청자 계좌 정보 수정 모달 */}
      <AnimatePresence>
        {showCoApplicantAccountModal && selectedCoApplicant && (
          <ModalOverlay onClick={(e) => e.stopPropagation()}>
            <EditModal
              variants={modalVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  {selectedCoApplicant.client.name}님의 계좌 정보 수정
                </ModalTitle>
                <CloseButton onClick={closeCoApplicantAccountModal}>
                  &times;
                </CloseButton>
              </ModalHeader>

              <ModalContent>
                {coApplicantAccountError && (
                  <ErrorMessage>{coApplicantAccountError}</ErrorMessage>
                )}

                {shakingFields.bankCode ? (
                  <ShakingInputContainer>
                    <Label>
                      은행선택<Required>*</Required>
                    </Label>
                    <BankSelect
                      ref={bankSelectRef}
                      value={
                        coApplicantAccountInfo.bankCode
                          ? bankOptions.find(
                              (option) =>
                                option.label === coApplicantAccountInfo.bankCode
                            )
                          : null
                      }
                      options={bankOptions}
                      onChange={(selectedOption) => {
                        setCoApplicantAccountInfo((prev) => ({
                          ...prev,
                          bankCode: selectedOption
                            ? selectedOption.label
                            : null,
                        }));
                      }}
                      placeholder="은행을 선택해주세요"
                      classNamePrefix="select"
                      components={{
                        Option: CustomOption,
                        SingleValue: CustomSingleValue,
                      }}
                      isSearchable={true}
                      noOptionsMessage={() => "검색 결과가 없습니다"}
                      loadingMessage={() => "검색 중..."}
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: accountErrors.bankCode
                            ? "#ff0000"
                            : "#e5e5e5",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                    {accountErrors.bankCode && (
                      <ErrorText>{accountErrors.bankCode}</ErrorText>
                    )}
                  </ShakingInputContainer>
                ) : (
                  <InputContainer>
                    <Label>
                      은행선택<Required>*</Required>
                    </Label>
                    <BankSelect
                      ref={bankSelectRef}
                      value={
                        coApplicantAccountInfo.bankCode
                          ? bankOptions.find(
                              (option) =>
                                option.label === coApplicantAccountInfo.bankCode
                            )
                          : null
                      }
                      options={bankOptions}
                      onChange={(selectedOption) => {
                        setCoApplicantAccountInfo((prev) => ({
                          ...prev,
                          bankCode: selectedOption
                            ? selectedOption.label
                            : null,
                        }));
                      }}
                      placeholder="은행을 선택해주세요"
                      classNamePrefix="select"
                      components={{
                        Option: CustomOption,
                        SingleValue: CustomSingleValue,
                      }}
                      isSearchable={true}
                      noOptionsMessage={() => "검색 결과가 없습니다"}
                      loadingMessage={() => "검색 중..."}
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderColor: accountErrors.bankCode
                            ? "#ff0000"
                            : "#e5e5e5",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                    {accountErrors.bankCode && (
                      <ErrorText>{accountErrors.bankCode}</ErrorText>
                    )}
                  </InputContainer>
                )}

                {shakingFields.accountNumber ? (
                  <ShakingInputContainer>
                    <Label>
                      계좌번호<Required>*</Required>
                    </Label>
                    <BankInput
                      ref={accountNumberRef}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={coApplicantAccountInfo.accountNumber || ""}
                      onChange={(e) => {
                        setCoApplicantAccountInfo((prev) => ({
                          ...prev,
                          accountNumber: e.target.value,
                        }));
                      }}
                      placeholder="계좌번호를 입력해주세요"
                      onKeyDown={handleAccountNumberKeyDown}
                    />
                    {accountErrors.accountNumber && (
                      <ErrorText>{accountErrors.accountNumber}</ErrorText>
                    )}
                  </ShakingInputContainer>
                ) : (
                  <InputContainer>
                    <Label>
                      계좌번호<Required>*</Required>
                    </Label>
                    <BankInput
                      ref={accountNumberRef}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={coApplicantAccountInfo.accountNumber || ""}
                      onChange={(e) => {
                        setCoApplicantAccountInfo((prev) => ({
                          ...prev,
                          accountNumber: e.target.value,
                        }));
                      }}
                      placeholder="계좌번호를 입력해주세요"
                      onKeyDown={handleAccountNumberKeyDown}
                    />
                    {accountErrors.accountNumber && (
                      <ErrorText>{accountErrors.accountNumber}</ErrorText>
                    )}
                  </InputContainer>
                )}

                {shakingFields.accountHolder ? (
                  <ShakingInputContainer>
                    <Label>
                      예금주명<Required>*</Required>
                    </Label>
                    <BankInput
                      ref={accountHolderRef}
                      type="text"
                      value={coApplicantAccountInfo.accountHolder || ""}
                      onChange={(e) => {
                        setCoApplicantAccountInfo((prev) => ({
                          ...prev,
                          accountHolder: e.target.value,
                        }));
                      }}
                      placeholder="예금주명을 입력해주세요"
                    />
                    {accountErrors.accountHolder && (
                      <ErrorText>{accountErrors.accountHolder}</ErrorText>
                    )}
                  </ShakingInputContainer>
                ) : (
                  <InputContainer>
                    <Label>
                      예금주명<Required>*</Required>
                    </Label>
                    <BankInput
                      ref={accountHolderRef}
                      type="text"
                      value={coApplicantAccountInfo.accountHolder || ""}
                      onChange={(e) => {
                        setCoApplicantAccountInfo((prev) => ({
                          ...prev,
                          accountHolder: e.target.value,
                        }));
                      }}
                      placeholder="예금주명을 입력해주세요"
                    />
                    {accountErrors.accountHolder && (
                      <ErrorText>{accountErrors.accountHolder}</ErrorText>
                    )}
                  </InputContainer>
                )}

                <ModalButtonGroup>
                  <ModalCancelButton onClick={closeCoApplicantAccountModal}>
                    취소
                  </ModalCancelButton>
                  <ConfirmButton
                    onClick={saveCoApplicantAccountInfo}
                    disabled={isUpdatingCoApplicant}
                  >
                    {isUpdatingCoApplicant ? "저장 중..." : "저장"}
                  </ConfirmButton>
                </ModalButtonGroup>
              </ModalContent>
            </EditModal>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* 소송 취하 불가 안내 모달 */}
      <AnimatePresence>
        {showNotAllowedModal && (
          <ModalOverlay>
            <ModalContainer
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalTitle>소송 취하가 불가능합니다</ModalTitle>
              <ModalContent>
                소장이 접수되어 소송이 진행 중인 상태입니다.
                <br />
                진행 중인 사건은 더 이상 온라인에서 취하하실 수 없습니다.
                <br />
                자세한 사항은 하단 카카오채널로 문의해 주세요.
              </ModalContent>
              <ModalButtonGroup>
                <ModalConfirmButton
                  onClick={closeNotAllowedModal}
                  style={{ width: "100%" }}
                >
                  확인
                </ModalConfirmButton>
              </ModalButtonGroup>
            </ModalContainer>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </PageBackground>
  );
};

export default StatusPage;
